<script>
  import { onMount, getContext } from 'svelte';

  import Dropdown from '../../components/Dropdown.svelte';
  import LightBox from '../../components/LightBox.svelte';
  import LinkButton from '../../components/LinkButton.svelte';
  import Modal from '../../components/Modal.svelte';
  import SizeChart from '../../components/Product/SizeChart.svelte';
  import ProductImageCarousel from './ProductImageCarousel.svelte';
  import { webPSupport } from '../../stores/WebP';
  import BenefitsV2 from '../../components/LandingPage/BenefitsV2.svelte';

  import determineProductColourImageUrl from '../../utils/determineProductColourImageUrl';
  import determinePreviewImagePrintDetails from '../../utils/determinePreviewImagePrintDetails';
  import { determineProductFromPrice } from '../../utils/pricing';
  import BulkPricingTable from '../../components/Product/BulkPricingTable.svelte';
  import QuoteButton from '../../components/QuoteButton.svelte';

  export let sizes = [];
  export let details;
  export let selectedGender;
  export let colourPathName;

  let showVersion = false;
  let showSize = false;
  let currTab = 'productInfo';
  let showLightBox = false;
  let showModal = false;
  let selectedSize = '';
  let filteredSizes = [];
  let filteredColors = [];
  let selectedGenderObject = { value: '', label: '' };
  let selectedColour = '';
  let genders = [];
  let sceneImages = [];
  let carouselImages = [];
  let productdescription = '';
  let productDetails = [];

  const collectionSlug = details.ProductCollection.slug;
  const categorySlug = details.ProductCollection.ProductCategory.slug;
  const ext = webPSupport ? 'webp' : 'jpg';

  const isSublimationProduct = details.Product.isSublimationProduct;

  const defaultBulkDiscounts = getContext('defaultBulkDiscounts');
  const productSpecificBulkDiscounts = details.Product.BulkPricingDiscounts;

  const displayPrice = determineProductFromPrice(
    details.Product.price,
    productSpecificBulkDiscounts,
    defaultBulkDiscounts,
  );

  // Convert description line breaks to <br>
  function determineDescription(details) {
    if (details.description && typeof details.description === 'string') {
      return details.description.replace(/(?:\r\n|\r|\n)/g, '<br>');
    }
    return '';
  }

  // Convert "details" text lines to <li>
  function determineProductDetails(details) {
    if (details.Product.details && typeof details.Product.details === 'string') {
      let detailsString = details.Product.details.replace(/(?:\r\n|\r|\n)/g, '<br>');
      let detailsArray = detailsString.split('<br>');
      let detailsList = [];
      // skip the last empty split
      for (let i = 0; i < detailsArray.length - 1; i++) {
        detailsList.push({ text: detailsArray[i] });
      }
      return detailsList;
    }
    return [];
  }

  async function createCarouselImagesArray({ sceneImages, colourObject, selectedGender, useCoverImage = false }) {
    const images = [];

    if (colourObject && colourObject[selectedGender]) {
      const { printType: printTypeFront, printPosition: printPositionFront } = determinePreviewImagePrintDetails(
        details.Product.printPositions,
        categorySlug,
        'front',
        selectedGender,
      );

      const { printType: printTypeBack, printPosition: printPositionBack } = determinePreviewImagePrintDetails(
        details.Product.printPositions,
        categorySlug,
        'back',
        selectedGender,
      );
      const frontImage = await determineProductColourImageUrl({
        productSlug: details.Product.slug,
        colourPathName: colourObject.pathName,
        availableGenders: genders.map((gender) => gender.value),
        colourObject,
        imageSize: '512',
        imageType: ext,
        gender: selectedGender,
        side: 'front',
        showModel: true,
        isModelBasedImages: details.Product.isModelBasedImages,
        useCoverImageForPreview: useCoverImage,
        sublimationTemplateId: details.Product.Product_SublimationTemplates.find((x) => x.isDefault)
          ?.SublimationTemplate?.id,
        previewImageDetails: {
          categorySlug,
          collectionSlug,
          printType: printTypeFront,
          printPosition: printPositionFront,
        },
      });
      images.push(frontImage);

      const backImage = await determineProductColourImageUrl({
        productSlug: details.Product.slug,
        colourPathName: colourObject.pathName,
        colourObject,
        imageSize: '512',
        imageType: ext,
        gender: selectedGender,
        side: 'back',
        showModel: true,
        isModelBasedImages: details.Product.isModelBasedImages,
        sublimationTemplateId: details.Product.Product_SublimationTemplates.find((x) => x.isDefault)
          ?.SublimationTemplate?.id,
        previewImageDetails: {
          categorySlug,
          collectionSlug,
          printType: printTypeBack,
          printPosition: printPositionBack,
        },
      });
      images.push(backImage);
    }

    // if there are scene images, add them to the carousel
    if (!sceneImages) return images;
    sceneImages.forEach((image) => {
      images.push(image[ext].w512);
    });

    return images;
  }

  async function updateCarouselImages(colourPathName, useCoverImage = false) {
    let displayColourPathName = colourPathName ? colourPathName : details.Product.displayColourPath;
    let selectedColourObject = details.Product.Product_Colours.find(
      (colour) => colour.pathName === displayColourPathName,
    );
    if (!selectedColourObject) {
      selectedColourObject = details.Product.Product_Colours[0];
    }
    carouselImages = await createCarouselImagesArray({
      sceneImages,
      colourObject: selectedColourObject,
      selectedGender: selectedGenderObject.value,
      useCoverImage,
    });
    selectedColour = selectedColourObject.displayName;
  }

  // Called on color click - also must be async to await the new images
  async function changeColor() {
    selectedColour = this.dataset.colorname;
    let selectedColourObject = details.Product.Product_Colours.find((colour) => colour.displayName === selectedColour);
    if (selectedColourObject) {
      carouselImages = await createCarouselImagesArray({
        sceneImages,
        colourObject: selectedColourObject,
        selectedGender: selectedGenderObject.value,
      });
    }
  }

  function changeSelectedSize() {
    selectedSize = this.textContent;
    showSize = false;
  }

  function filterSizeByGender(gender) {
    filteredSizes = sizes.filter((size) => size.ProductSize.gender === gender);
    if (filteredSizes.length > 0) {
      const size = filteredSizes[0];
      selectedSize = `${size.ProductSize.name.toUpperCase()} (${size.width} ${size.units})`;
    } else {
      selectedSize = '';
    }
  }

  function filterColorsByGender(gender) {
    filteredColors = details.Product.Product_Colours.filter((color) => color[gender]);
  }

  async function changeSelectedGender() {
    selectedGenderObject = {
      value: this.dataset.value,
      label: this.textContent,
    };
    filterSizeByGender(selectedGenderObject.value);
    filterColorsByGender(selectedGenderObject.value);
    // Re-generate images for this gender
    await updateCarouselImages(details.Product.useCoverImageForPreview);
  }

  function populateGenders(product, selectedGender) {
    if (product.mensAvailable) genders.push({ label: 'Men', value: 'mens' });
    if (product.womensAvailable) genders.push({ label: 'Women', value: 'womens' });
    if (product.youthAvailable) genders.push({ label: 'Youth', value: 'youth' });

    if (selectedGender) {
      selectedGenderObject = genders.find((g) => g.value === selectedGender) || genders[0];
    } else {
      selectedGenderObject = genders[0]; // Default to the first available if none selected
    }
    filterSizeByGender(selectedGenderObject.value);
    filterColorsByGender(selectedGenderObject.value);
  }

  function carouselImageClick() {
    showLightBox = true;
  }

  function changeTab(selectedTab = 'productInfo') {
    currTab = selectedTab;
  }

  let showPricingModal;
  function handlePricingDetailsClick() {
    showPricingModal = true;
  }

  onMount(async () => {
    sceneImages = JSON.parse(details.Product.sceneImages);
    populateGenders(details.Product, selectedGender);
    productdescription = determineDescription(details);
    productDetails = determineProductDetails(details);
    await updateCarouselImages(colourPathName, details.Product.useCoverImageForPreview);
  });
</script>

<section>
  <div class="container">
    <div class="left">
      <div class="product-preview">
        <!-- Use a key so if carouselImages updates, it re-renders properly -->
        {#key carouselImages}
          <ProductImageCarousel
            {carouselImages}
            handleImageClick={carouselImageClick}
            {collectionSlug}
            {categorySlug}
            isModelBasedImages={details.Product.isModelBasedImages}
          />
        {/key}
      </div>
      <LightBox bind:show={showLightBox} images={sceneImages} />
    </div>

    <div class="buttons mobile-button">
      <LinkButton
        to={`/design/${details.slug}/${isSublimationProduct ? 'choose-template' : 'choose-colours'}`}
        label="start my design"
        type="orange"
        fontSize="large"
        ripple="ripple"
      />
      <QuoteButton alternate marginReset />
      <BenefitsV2 allowModal={true} {isSublimationProduct} />
    </div>
    <div class="right">
      <div class="custom-text">Custom Printed</div>
      <div class="supplier">{details.Product.Supplier.displayName}</div>
      <h1 class="product-name">{details.name}</h1>
      <div class="price">
        <div class="price-from">from</div>
        ${displayPrice}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="price-details" on:click={handlePricingDetailsClick}>
          Pricing Details<img class="info-icon" src="img/icons/info.svg" alt="" />
        </div>
        <!-- <del class="discount">${(details.compareAtPrice / 100).toFixed(2)}</del> -->
      </div>

      <div class="colors">
        <div class="section-title">
          <h4>Colour</h4>
        </div>
        <div class="colors-grid">
          {#each filteredColors as color, idx}
            <div class="color-container">
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                data-colorname={color.displayName}
                class="color"
                class:active={selectedColour === color.displayName}
                on:click={changeColor}
              >
                <span class="shade" style="background-color: rgb{color.ProductColoursMasterMain.rgb}" />

                {#if color.ProductColoursMasterTrim1}
                  <span class="shade" style="background-color: rgb{color.ProductColoursMasterTrim1.rgb}" />
                {/if}

                {#if color.ProductColoursMasterTrim2}
                  <span class="shade" style="background-color: rgb{color.ProductColoursMasterTrim2.rgb}" />
                {/if}

                {#if color.ProductColoursMasterTrim3}
                  <span class="shade" style="background-color: rgb{color.ProductColoursMasterTrim3.rgb}" />
                {/if}
              </div>
              <div class="tooltip">{color.displayName}</div>
            </div>
          {/each}
        </div>
      </div>

      <div class="types">
        <div class="section-title">
          <h4>Size</h4>
        </div>
        <div class="types-options">
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="option" on:click={() => (showVersion = !showVersion)}>
            {selectedGenderObject.label}
            <img src="img/icons/triangle.svg" alt="" />
            <Dropdown bind:show={showVersion}>
              {#each genders as gender}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div data-value={gender.value} on:click={changeSelectedGender}>
                  {gender.label}
                </div>
              {/each}
            </Dropdown>
          </div>
          <div class="option">
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div class="center-h" on:click={() => (showSize = !showSize)}>
              {selectedSize}
              <img src="img/icons/triangle.svg" alt="" />
            </div>
            <Dropdown bind:show={showSize}>
              {#each filteredSizes as size}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div class="size-item" on:click={changeSelectedSize}>
                  {size.ProductSize.name.toUpperCase()} ({size.width}
                  {size.units})
                </div>
              {/each}
            </Dropdown>
          </div>
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="size" on:click={() => (showModal = !showModal)}>
            View Size Chart <img class="info-icon" src="img/icons/info.svg" alt="" />
          </div>
        </div>
      </div>
      <Modal bind:show={showModal} widthLg={true}>
        <div class="size-guide">
          <h3>Size Guide and Chart</h3>
          <img
            src="https://images.uniformhero.com.au/assets/web/size-guide.jpg"
            alt="guide on how to measure half chest sizes"
          />
          <div class="size-guide-text">
            Take an existing shirt that fits you well, lay it flat on a table and measure the half-chest (5cm under the
            armpit). Then check that measurement against the product sizes on our website to select the best size for
            your item.
          </div>
        </div>
        <div class="sizes">
          {#if details.Product.mensAvailable}
            <SizeChart {sizes} genderToShow="mens" />
          {/if}
          {#if details.Product.womensAvailable}
            <SizeChart {sizes} genderToShow="womens" />
          {/if}
          {#if details.Product.youthAvailable}
            <SizeChart {sizes} genderToShow="youth" />
          {/if}
        </div>
      </Modal>

      <div class="buttons fullscreen-button">
        <LinkButton
          to={`/design/${details.slug}/${isSublimationProduct ? 'choose-template' : 'choose-colours'}`}
          label="start my design"
          type="orange"
          fontSize="large"
          ripple="ripple"
        />
        <QuoteButton alternate />
        <!-- <span>or</span>
                  <LinkButton label="buy blank" to="/" type="black-o" /> -->
      </div>
      <div class="tab">
        <div class="labels">
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <span class:active={currTab === 'productInfo'} on:click={() => changeTab('productInfo')}
            >Product Information</span
          >
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <span class:active={currTab === 'description'} on:click={() => changeTab('description')}>Description</span>
        </div>
        <div class="content">
          {#if currTab === 'productInfo'}
            <ul>
              {#each productDetails as detail}
                <li>
                  <span>{detail.text}</span>
                </li>
              {/each}
            </ul>
          {:else if currTab === 'description'}
            {@html productdescription}
          {/if}
        </div>
      </div>
    </div>
  </div>
</section>
<div class="benefits-fullscreen">
  <BenefitsV2 allowModal={true} {isSublimationProduct} />
</div>

<Modal bind:show={showPricingModal} widthLg={true}>
  <BulkPricingTable
    basePriceInCents={details.Product.price}
    discounts={productSpecificBulkDiscounts.length ? productSpecificBulkDiscounts : defaultBulkDiscounts}
  />
</Modal>

<style>
  .right {
    margin-top: 4rem;
  }

  .center-h {
    display: flex;
    align-items: center;
  }

  .sizes {
    margin: 0 auto;
    max-width: 100rem;
    width: 95%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 4rem;
  }

  .container {
    width: 90%;
    max-width: 120rem;
    margin: 0 auto 5rem auto;
    display: flex;
    flex-direction: column;
  }

  .size-item {
    text-align: left;
    text-transform: uppercase;
  }

  h4 {
    margin-bottom: 0.5rem;
    letter-spacing: 1px;
    color: #8c8c8c;
    font-size: 1.2rem;
    text-transform: uppercase;
  }

  h1 {
    font-size: 5rem;
    text-transform: uppercase;
    font-family: 'Oswald', sans-serif;
  }

  .custom-text {
    color: #fe5900;
    text-transform: uppercase;
    font-weight: 600;
  }

  .supplier {
    font-family: 'Oswald';
    text-transform: uppercase;
    font-size: 1.6rem;
    color: #535353;
    margin-bottom: -1rem;
  }

  .product-preview {
    min-height: 30rem;
    position: relative;
  }

  .price {
    display: flex;
    align-items: center;
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    color: #fe5900;
    font-size: 3rem;
  }

  .price-from {
    font-size: 1.3rem;
    margin-right: 0.5rem;
  }

  .price-details {
    display: flex;
    align-items: center;
    margin-left: 2rem;
    font-size: 1.5rem;
    color: #1c2a5c;
    cursor: pointer;
  }

  .info-icon {
    height: 20px;
    margin-left: 5px;
  }

  /* .price .discount {
    color: #d1d1d1;
    font-size: 1.6rem;
  } */

  .tab {
    margin-top: 2rem;
    border-bottom: 1px solid #ddd;
    padding-bottom: 2rem;
  }

  .labels {
    text-transform: uppercase;
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    color: #a7a7a7;
    margin-bottom: 2rem;
    border-bottom: 1px solid #eee;
  }

  .labels span {
    display: inline-block;
    cursor: pointer;
    padding-bottom: 1rem;
  }

  .labels span.active {
    color: #000;
    border-bottom: 1px solid #000;
  }

  .labels span:not(:last-child) {
    margin-right: 2rem;
  }

  .tab .content {
    color: #787878;
    font-size: 1.8rem;
    margin-left: 2rem;
  }

  .types {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  .types-options {
    display: flex;
    justify-content: space-between;
  }

  .option {
    text-align: center;
    padding: 1.5rem;
    border: 1px solid #eee;
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    position: relative;
  }

  .option {
    min-width: 10rem;
  }

  .option img {
    width: 1rem;
    margin-left: 0.5rem;
  }

  .size {
    font-family: 'Oswald';
    font-size: 1.7rem;
    display: flex;
    align-items: center;
    margin-left: 1rem;
    color: #1c2a5c;
    cursor: pointer;
  }
  .size-guide {
    display: flex;
    flex-direction: column;
    margin-bottom: 4rem;
    margin-left: 4rem;
    margin-right: 4rem;
    align-items: center;
    gap: 2rem;
  }
  .size-guide h3 {
    text-transform: uppercase;
    font-family: 'Oswald';
    color: #fe5900;
  }

  .size-guide img {
    width: 250px;
  }

  .option:last-child {
    margin-top: 2rem;
  }

  .colors {
    margin-top: 1rem;
  }
  .colors-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, 3.5rem);
    max-width: 60rem;
    width: 100%;
    gap: 1rem;
    align-items: center;
    margin-bottom: 3rem;
  }

  .colors .color-container {
    width: 3.5rem;
    height: 3.5rem;
    position: relative;
  }

  .colors .color {
    border-radius: 1rem;
    box-shadow: 0 0 0.5rem 0 rgba(0, 0, 0, 0.15);
    border: 1px solid #eee;
    cursor: pointer;
    display: flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .color-container .tooltip {
    position: absolute;
    top: -3.7rem;
    left: 0;
    border-radius: 0.5rem;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-weight: bold;
    font-size: 1.2rem;
    text-align: center;
    padding: 1rem;
    display: none;
  }

  .colors .color.active {
    border: 3px solid #fe5900;
  }

  .color-container:hover .tooltip {
    display: block;
  }

  .colors .color .shade {
    flex: 1;
  }

  .buttons {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .buttons.fullscreen-button {
    display: none;
  }

  .benefits-fullscreen {
    display: none;
  }

  .buttons.mobile-button {
    display: grid;
    margin-top: 1rem;
  }

  @media screen and (min-width: 700px) {
    .option:last-child {
      margin-top: 0;
      margin-left: 5rem;
    }
  }
  @media screen and (min-width: 900px) {
    .container {
      flex-direction: row;
    }
    .right {
      width: 50%;
      margin-left: 5rem;
      margin-top: 0;
    }

    .left {
      width: 50%;
    }

    .option {
      width: auto;
    }

    .buttons {
      display: block;
    }

    .buttons.fullscreen-button {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .benefits-fullscreen {
      display: block;
    }

    .buttons.mobile-button {
      display: none;
    }
  }
</style>
